import React, { useRef, useState } from 'react';
import { FaFileUpload, FaFile, FaTrash, FaExclamationCircle } from 'react-icons/fa';

const DocumentUpload = ({ files, onFileChange }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');

  const ALLOWED_TYPES = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'image/jpeg': '.jpg, .jpeg',
    'image/png': '.png'
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateFiles = (fileList) => {
    const validFiles = [];
    let errorMessage = '';

    Array.from(fileList).forEach(file => {
      if (!Object.keys(ALLOWED_TYPES).includes(file.type)) {
        errorMessage = `Invalid file type: ${file.name}. Please use supported formats.`;
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        errorMessage = `File too large: ${file.name}. Maximum size is 10MB.`;
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errorMessage };
  };

  const handleFiles = (fileList) => {
    const { validFiles, errorMessage } = validateFiles(fileList);
    
    if (errorMessage) {
      setError(errorMessage);
      setTimeout(() => setError(''), 5000); // Clear error after 5 seconds
      return;
    }

    onFileChange([...files, ...validFiles]);
  };

  const handleFileSelect = (e) => {
    e.preventDefault();
    handleFiles(e.target.files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeFile = (indexToRemove) => {
    onFileChange(files.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="document-upload">
      <h3>Supporting Documents</h3>
      
      <div 
        className={`upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          className="file-input"
          accept={Object.values(ALLOWED_TYPES).join(',')}
        />
        
        <div className="upload-content">
          <FaFileUpload className="upload-icon" />
          <h4>Drag and drop files here</h4>
          <p>or</p>
          <button 
            type="button"
            className="upload-button"
            onClick={() => fileInputRef.current.click()}
          >
            Choose Files
          </button>
          
          <p className="upload-hint">
            Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
            <br />
            Maximum file size: 10MB
          </p>
        </div>
      </div>

      {error && (
        <div className="upload-error">
          <FaExclamationCircle />
          <span>{error}</span>
        </div>
      )}

      {files.length > 0 && (
        <div className="file-list">
          {files.map((file, index) => (
            <div key={index} className="file-item">
              <div className="file-info">
                <FaFile className="file-icon" />
                <span className="file-name">{file.name}</span>
                <span className="file-size">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <button
                type="button"
                className="remove-file"
                onClick={() => removeFile(index)}
                aria-label={`Remove ${file.name}`}
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload; 